/**
 * Where Lomasaaret already exists on the web, in one place.
 *
 * The map, the "open in Google Maps" link, directions and the ratings on other sites all come
 * from here, so a change - a new Booking.com score, a moved pin - is one edit instead of a hunt
 * through four components.
 */

/**
 * Google's own pin for the estate's Maps listing (the one with 66 reviews), read from the
 * listing on 9 Oct 2026. It agrees with the JSON-LD geo in index.html to three decimals.
 *
 * Coordinates rather than an address query on purpose. The address query the contact page used
 * before ("Lomasaaret Cottages, Joensuuntie 3186, 58200 Kerimäki") does not resolve - the embed
 * showed a stretch of lake with no pin at all. These are the coordinates of the very listing a
 * guest navigates to from Google, checked by eye on the rendered map.
 */
export const ESTATE_PIN = { lat: 62.0331889, lng: 29.5162058 } as const

/**
 * The Maps listing itself. A cid link opens the place card - name, rating, the 66 reviews,
 * guests' photos - rather than a search that may or may not land on it.
 */
export const GOOGLE_LISTING_URL = 'https://maps.google.com/?cid=2329787872640560723'

const pin = `${ESTATE_PIN.lat},${ESTATE_PIN.lng}`

/**
 * Google's own embed for the listing, from Maps → Share → "Embed a map" (9 Oct 2026).
 *
 * Not a coordinate query: a bare coordinate has no place behind it, so the embed's info box
 * reads "Place info couldn't load" - which is what the first version showed. This one names the
 * listing (its place id is the `1s0x469bb4ec57d8980f:0x205511b9f6c0ee53` part), so the box shows
 * the name, the address and "4.4 ★ (66)" with links to the listing and to directions. No API key.
 *
 * The two `1s<lang>!2sfi` segments are the map's language and region; the language follows the
 * site so the map labels read in the guest's own language. `1d6000` is the view's span in metres -
 * wide enough to show the lake and road 71, which is how people recognise the turn.
 */
const EMBED_PB_HEAD =
  '!1m18!1m12!1m3!1d6000!2d29.516205799999998!3d62.0331889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1' +
  '!3m3!1m2!1s0x469bb4ec57d8980f%3A0x205511b9f6c0ee53' +
  '!2zTG9tYXNhYXJldCAo0L7RgtC00YvRhSDQvdCwINC-0YHRgtGA0L7QstCw0YUpOiDQutC-0YLRgtC10LTQttC4LCDQs9C-0YHRgtC40L3QuNGG0LAsINC60LDRhNC1LCDRgNGL0LHQsNC70LrQsA' +
  '!5e0'

export function mapEmbedUrl(language: string): string {
  const lang = encodeURIComponent(language || 'en')
  return `https://www.google.com/maps/embed?pb=${EMBED_PB_HEAD}!3m2!1s${lang}!2sfi!4v1791529563715!5m2!1s${lang}!2sfi`
}

export const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${pin}`

export interface ExternalListing {
  /** Shown as-is: these are brand names and do not translate. */
  platform: 'Google' | 'Booking.com' | 'Airbnb' | 'Tripadvisor'
  /** Which listing, when a platform has more than one. Translation key under reviews.listing.* */
  listing?: 'rooms' | 'cottages' | 'mainHouse' | 'smallCottage'
  rating: number
  /** Booking.com scores out of 10; everyone else out of 5. Shown in the platform's own scale. */
  scale: 5 | 10
  count: number
  url: string
}

/**
 * Ratings as published on each site, read on 9 Oct 2026.
 *
 * Numbers, not review texts. A rating and a count, credited and linked, is a fact about the
 * estate; copying the reviews themselves would be republishing other people's writing, which
 * the platforms' terms forbid. The link is how a guest reads them.
 *
 * Shown as "reviews on other sites", not as booking buttons: the site exists so bookings come
 * direct, and a "book on Booking.com" button would send guests back to the commission.
 * Refresh these by hand now and then; README says where.
 */
export const RATINGS_AS_OF = '2026-10-09'

export const EXTERNAL_LISTINGS: ExternalListing[] = [
  { platform: 'Google', rating: 4.4, scale: 5, count: 66, url: GOOGLE_LISTING_URL },
  {
    platform: 'Airbnb',
    listing: 'smallCottage',
    rating: 4.48,
    scale: 5,
    count: 60,
    url: 'https://www.airbnb.com/rooms/19429847',
  },
  {
    platform: 'Booking.com',
    listing: 'rooms',
    rating: 7.5,
    scale: 10,
    count: 41,
    url: 'https://www.booking.com/hotel/fi/lomasaaret.html',
  },
  {
    platform: 'Booking.com',
    listing: 'cottages',
    rating: 8.3,
    scale: 10,
    count: 22,
    url: 'https://www.booking.com/hotel/fi/lomasaaret-cottages.html',
  },
  {
    platform: 'Airbnb',
    listing: 'mainHouse',
    rating: 4.7,
    scale: 5,
    count: 20,
    url: 'https://www.airbnb.com/rooms/790716',
  },
  {
    platform: 'Tripadvisor',
    rating: 4.3,
    scale: 5,
    count: 6,
    url: 'https://www.tripadvisor.com/Hotel_Review-g189911-d6160413-Reviews-Lomasaaret_Hotel-Savonlinna_Southern_Savonia.html',
  },
]

export const EXTERNAL_REVIEW_TOTAL = EXTERNAL_LISTINGS.reduce((sum, l) => sum + l.count, 0)
